import { http } from "./client";
import type { PageParams } from "@/types/api";
import type {
  Department,
  DepartmentAssignment,
  DepartmentEmployees,
  DepartmentHierarchy,
  DepartmentHistory,
  DepartmentManagers,
  DepartmentSummary,
  EntityStatus,
} from "@/types/domain";

export interface DepartmentFilters extends PageParams {
  search?: string;
  // NOTE: ListDepartments does not apply a server-side status filter today
  // (see backend/internal/transport/http/handler/workspace.go's
  // ListDepartments, which only reads `search`) - a caller that wants
  // "active only" needs to filter the returned page client-side. Kept on
  // the type for forward-compatibility, but DepartmentsPage does not wire a
  // status Select against it for exactly this reason.
  status?: EntityStatus | "";
}

// head_employee_id is deliberately absent here - PUT /departments/:id no
// longer accepts it (silently ignored if sent). Head assignment after
// creation goes through departmentsApi.setHead only, which records the
// change in DepartmentHeadChange history; parent_id is accepted here (a
// change from before).
export type DepartmentInput = Partial<Pick<Department, "name" | "code" | "description" | "cost_center" | "status" | "parent_id">> & {
  /** Department attendance mode; "" clears it (inherit the organization default). */
  default_attendance_mode?: "normal" | "geo_fencing" | "field_check_in" | "check_in_off" | "";
};

// The one exception: an initial head may still be chosen at creation - POST
// /departments still accepts head_employee_id even though PUT no longer does.
export type DepartmentCreateInput = DepartmentInput & { head_employee_id?: string };

// BulkAssignResult mirrors service.BulkTransferDepartmentResult. succeeded/
// failed are typed nullable even though the backend now always initializes
// both as a real (possibly empty) array, never a bare zero-value slice that
// Go's encoding/json would serialize as `null` (see that struct's own doc
// comment for the incident this guards) - callers reading this type should
// not have to trust that invariant never regresses, or that every future
// producer of this shape (another handler, a mock in a test) gets it right
// either.
export interface BulkAssignResult {
  succeeded: string[] | null;
  failed: { employee_id: string; reason: string }[] | null;
}

/** A department's designation with how many on-roll employees hold it. `id` is
 * absent for a title employees carry that has no row in the Designations master. */
export interface DepartmentDesignation {
  id?: string;
  name: string;
  code: string;
  level?: number | null;
  status: string;
  employee_count: number;
  in_master: boolean;
}

export const departmentsApi = {
  list: (params: DepartmentFilters = {}) => http.getPage<Department>("/departments", params),
  get: (id: string) => http.get<Department>(`/departments/${id}`),
  create: (input: DepartmentCreateInput) => http.post<Department>("/departments", input),
  update: (id: string, input: DepartmentInput) => http.put<Department>(`/departments/${id}`, input),
  remove: (id: string) => http.delete<void>(`/departments/${id}`),
  setStatus: (id: string, status: EntityStatus) => http.patch<Department>(`/departments/${id}/status`, { status }),
  summary: () => http.get<DepartmentSummary>("/departments/summary"),
  // The department(s) the caller explicitly heads - a Manager's "assigned
  // team(s)" (Manager RBAC spec section 6). Self-scoped server-side, safe
  // for any role; usually empty for a role that isn't anyone's department
  // head. Backs MyDepartmentRedirect - replaces the old "resolve my own
  // department_id and open it" shortcut, which opened whatever department
  // the caller's employee record sat in, not the team(s) they actually head.
  myTeams: () => http.get<{ items: Department[] }>("/departments/my-teams"),
  // branch_id narrows the headcounts to one branch's people.
  designations: (id: string, params: { branch_id?: string; search?: string } = {}) =>
    http.get<DepartmentDesignation[]>(`/departments/${id}/designations`, params),
  hierarchy: (id: string) => http.get<DepartmentHierarchy>(`/departments/${id}/hierarchy`),
  employees: (id: string) => http.get<DepartmentEmployees>(`/departments/${id}/employees`),
  managers: (id: string) => http.get<DepartmentManagers>(`/departments/${id}/managers`),
  history: (id: string) => http.get<DepartmentHistory>(`/departments/${id}/history`),
  // head_employee_id is required on the wire even when clearing - pass ""
  // to remove the current head, matching the backend's own contract.
  setHead: (id: string, input: { head_employee_id: string; effective_date: string; reason?: string }) =>
    http.patch<Department>(`/departments/${id}/head`, input),
  bulkAssign: (id: string, input: { employee_ids: string[]; effective_date: string; reason?: string }) =>
    http.post<BulkAssignResult>(`/departments/${id}/employees/bulk-assign`, input),
  // Department-scope ABAC admin surface (migration 0123) - see
  // branchesApi.assignments' doc comment, its structurally identical
  // sibling. GET only needs department.read; POST/DELETE require
  // department.write, matching router.go's gating exactly.
  assignments: {
    list: (departmentId: string) =>
      http.get<{ assignments: DepartmentAssignment[] }>(`/departments/${departmentId}/assignments`),
    add: (departmentId: string, userId: string) =>
      http.post<{ assignment: DepartmentAssignment }>(`/departments/${departmentId}/assignments`, { user_id: userId }),
    remove: (departmentId: string, userId: string) =>
      http.delete<void>(`/departments/${departmentId}/assignments/${userId}`),
  },
};
